// src/hooks/useReporteSnapshot.js
import { useEffect, useState } from "react";
import { buildUltimoPuntoUrl, buildRecorridoUrl } from "../utils/buildHereStaticMapUrls";
import axios from "axios";

const LS_DATA_KEY = "reporte:contrato:data";

/* -------------------- Helpers -------------------- */

// JSON safe
function safeParse(json) { try { return JSON.parse(json); } catch { return null; } }

// Lee contrato cacheado si coincide
function getContratoBaseFromLS(contractIdParam) {
    const raw = localStorage.getItem(LS_DATA_KEY);
    const obj = raw ? safeParse(raw) : null;
    if (obj?.ContractID && (!contractIdParam || contractIdParam === obj.ContractID)) return obj;
    return null;
}

// Color por score numérico
function getScoreColor(scoreNum) {
    const s = Number(scoreNum) || 0;
    if (s === 1) return "green";
    if (s === 2) return "yellow";
    if (s >= 3) return "red";
    return "transparent";
}

/** filename en minúsculas, sin ruta ni query */
function onlyFileName(x = "") {
    const raw = String(x || "").split("?")[0];
    const last = raw.split(/[\\/]/).pop() || "";
    try { return decodeURIComponent(last).toLowerCase(); } catch { return last.toLowerCase(); }
}

/** Convierte imagen a base64 (para evitar CORS al imprimir). Si falla => null */
async function urlToDataURL(url) {
    if (!url) return null;
    try {
        const res = await axios.get(url, { responseType: "blob", withCredentials: false });
        const blob = res.data;
        return await new Promise((resolve) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result);
            r.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}

/**
 * Enriquecer fotos y crear índice:
 *  - fotos: cada una con SourceBase64
 *  - fotoIndex: map por filename normalizado de photo y de Source
 */
async function enrichFotosWithBase64AndIndex(fotosEntrada) {
    const arr = Array.isArray(fotosEntrada) ? fotosEntrada : [];
    const out = [];
    const index = {};

    for (const f of arr) {
        const base64 = await urlToDataURL(f?.Source);
        const enriched = { ...f, SourceBase64: base64 || null };
        const k1 = onlyFileName(f?.photo || "");
        const k2 = onlyFileName(f?.Source || "");
        if (k1) index[k1] = enriched;
        if (k2) index[k2] = enriched;
        out.push(enriched);
    }
    return { fotos: out, fotoIndex: index };
}

/**
 * Construye comparision usando EXCLUSIVAMENTE fotoIndex (no se sale a red).
 * Cada evento queda como { imageUrl, imageBase64, bounding_box, related_objects, meta }
 */
function normalizeBB(bb) {
    if (!bb) return null;
    const norm = (v) => typeof v === "number" && v >= 0 && v <= 1;
    const has = (k) => typeof bb[k] === "number";
    if (norm(bb.top) && norm(bb.left)) {
        if (has("width") && has("height")) return { top: bb.top, left: bb.left, width: bb.width, height: bb.height };
        if (has("right") && has("bottom")) return { top: bb.top, left: bb.left, width: bb.right - bb.left, height: bb.bottom - bb.top };
    }
    return null;
}

async function buildComparisionArray(assetListRaw, fotoIndex) {
    const assets = Array.isArray(assetListRaw) ? assetListRaw : (assetListRaw ? [assetListRaw] : []);
    const comparisionData = [];

    for (const asset of assets) {
        if (!asset || !Array.isArray(asset.comparison)) continue;

        for (const comp of asset.comparison) {
            const events = comp.events || {};
            const results = comp.results || {};
            const scores = results?.scores || {};

            const enrichedEvents = Object.entries(events).map(([eventKey, eventVal]) => {
                let imageName = null;
                let bbox = null;
                let related = null;

                if (typeof eventVal === "string") {
                    imageName = eventVal;
                } else if (eventVal && typeof eventVal === "object") {
                    imageName = eventVal.image;
                    bbox = eventVal.bounding_box || eventVal.boundingBox || null;
                    related = eventVal.related_objects || eventVal.relatedObjects || [];
                }

                const norm = onlyFileName(imageName);
                const fotoInfo = norm ? fotoIndex[norm] : null; // *** SÓLO de fotos ya cargadas ***

                return {
                    key: eventKey,
                    imageUrl: fotoInfo?.Source || null,
                    imageBase64: fotoInfo?.SourceBase64 || null,
                    bounding_box: normalizeBB(bbox),
                    related_objects: Array.isArray(related) ? related : [],
                    meta: {
                        Evento: fotoInfo?.Evento ?? null,
                        Hora: fotoInfo?.Hora ?? null,
                        Latitud: fotoInfo?.Latitud ?? null,
                        Longitud: fotoInfo?.Longitud ?? null,
                        Geo: fotoInfo?.Geo ?? null,
                    },
                };
            });

            comparisionData.push({
                assetId: asset.ID,
                assetType: asset.type,
                scoreColor: getScoreColor(scores?.status),
                results,               // {status, alerts, container_closure, physical_condition, ...}
                enrichedEvents,        // listo para pintar
            });
        }
    }
    return comparisionData;
}

/* -------------------- Hook principal -------------------- */

export default function useReporteSnapshot(contractIdParam) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snapshot, setSnapshot] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setError(null);

                const token = localStorage.getItem("tkn");

                // 1) contrato
                let contratoBase = getContratoBaseFromLS(contractIdParam);
                if (!contratoBase) {
                    const resContrato = await axios.post(
                        `${process.env.REACT_APP_SERVER}/operaciones/getcontratounico`,
                        JSON.stringify({ contrato: contractIdParam }),
                        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, withCredentials: true }
                    );
                    if (resContrato.data?.success === false) throw new Error(resContrato.data?.message || "No se pudo obtener el contrato");
                    contratoBase = resContrato.data?.data?.[0] || null;
                    if (!contratoBase) throw new Error("Contrato vacío");
                    localStorage.setItem(LS_DATA_KEY, JSON.stringify(contratoBase));
                }

                const contractID = contratoBase.ContractID;

                // 2) fotos
                const resFotos = await axios.post(
                    `${process.env.REACT_APP_SERVER}/operaciones/getfotoscontrato`,
                    JSON.stringify({ contrato: contractID }),
                    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, withCredentials: true }
                );
                const fotosRaw = resFotos.data?.success ? (resFotos.data?.data || []) : [];
                const { fotos: fotosConBase64, fotoIndex } = await enrichFotosWithBase64AndIndex(fotosRaw);

                // 3) visual logistic (comparision)
                const resVisual = await axios.post(
                    `${process.env.REACT_APP_SERVER}/operaciones/getcontractvisuallogistic`,
                    JSON.stringify({ contrato: contractID }),
                    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, withCredentials: true }
                );
                const assetListRaw = resVisual.data?.info?.asset_list || [];
                const comparisionList = await buildComparisionArray(assetListRaw, fotoIndex);

                // 4) tabla tráfico
                const resTrafico = await axios.post(
                    `${process.env.REACT_APP_SERVER}/operaciones/getreportestrafico`,
                    JSON.stringify({ contrato: contractID }),
                    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, withCredentials: true }
                );
                const reportesTabla = resTrafico.data?.success ? (resTrafico.data?.data || []) : [];

                // 5) recorrido
                let recorridoData = [];
                if (contratoBase.FKLokTipoEquipo === 8) {
                    const resRecV = await axios.post(
                        `${process.env.REACT_APP_SERVER}/operaciones/getreportesdevicevalitronics`,
                        JSON.stringify({ contrato: contractID }),
                        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, withCredentials: true }
                    );
                    if (resRecV.data?.success) recorridoData = resRecV.data?.data || [];
                } else {
                    const bodyN = {
                        fechainicio: contratoBase.fecha,
                        fechafin: contratoBase.fechafin,
                        device: contratoBase.FKLokDeviceID,
                        utcMinutos: 5,
                        tipo: contratoBase.Active,
                        tipoequipo: contratoBase.FKLokTipoEquipo,
                    };
                    const resRecN = await axios.post(
                        `${process.env.REACT_APP_SERVER}/operaciones/getreportesdevicesimplificado`,
                        JSON.stringify(bodyN),
                        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, withCredentials: true }
                    );
                    if (resRecN.data?.success) recorridoData = resRecN.data?.data || [];
                }

                // 6) último punto
                let lastPoint = null;
                try {
                    const respLast = await axios.post(
                        `${process.env.REACT_APP_SERVER}/operaciones/getlatlngcontrato`,
                        JSON.stringify({ contrato: contractID }),
                        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, withCredentials: true }
                    );
                    if (respLast.data?.success && Array.isArray(respLast.data?.data) && respLast.data.data[0]) {
                        lastPoint = respLast.data.data[0];
                    }
                } catch { }

                // 7) mapas estáticos
                const ultimoPuntoStaticURL = buildUltimoPuntoUrl(lastPoint, contratoBase);
                const recorridoStaticURL = buildRecorridoUrl(recorridoData);

                // 8) snapshot final
                setSnapshot({
                    contrato: contratoBase,
                    fotos: fotosConBase64,
                    reportes: reportesTabla,
                    comparision: comparisionList,
                    mapas: { ultimoPuntoUrl: ultimoPuntoStaticURL, recorridoUrl: recorridoStaticURL },
                });
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError(err.message || "Error generando snapshot");
                setLoading(false);
            }
        })();
    }, [contractIdParam]);

    return { loading, error, snapshot };
}
